import Backbone from 'backbone'
import template from './logician.hbs'
import FormView from 'scoreboard/components/form-view'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import formHelper from 'scoreboard/components/form-helper'
import ConfigurableList from 'scoreboard/components/configurable-list'
import ConditionalStatementEditor from 'scoreboard/components/logic/conditional-statement-editor'

let collapseToggle = '<div class="collapse-toggle"></div>'

const Logician = Backbone.View.extend(/** @lends {Logician} */{

	className: 'logician',

	/** @type {FormView} */
	formView: null,

	events: {
		'click .collapse-toggle': 'toggleCollapse',
		'mouseenter': 'setFixedHeight'
	},

	/**
	 * The logician handles editing of if/else logic (#46581). It differs from
	 * the conditional-statement-editor in that it can handle multiple
	 * conditional statements instead of just one
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {ConditionalStatement[]} [options.statements] existing conditional
	 * statements to be rendered
	 *
	 * @param {FormView} [options.formView] the FormView that this Logician is
	 *
	 * @param {Function} options.conditionEditorCreator a Function that returns
	 * a new instance of an editor for a Condition. The Condition may be new or
	 * existing
	 *
	 * @param {Function} options.consequencesEditorCreator a Function that
	 * returns a new instance of an editor that can be used to create and edit
	 * Consequences
	 */
	initialize: function(options = {}) {
		this.options = options
		this.formView = options.formView ?? new FormView({
			el: this.el
		})
	},

	render: function() {
		this.$el.html(template())
		this.renderStatements()
		this.updateAddLabel()
		this.toggleElseTitle()
		if (!this.options.formView) {
			this.formView.start()
		}
		this.formView.on('change', () => this.resetFixedHeight())
		return this
	},

	renderStatements: function() {
		// render all the statements except the last one, which is the "else"
		// statement that is handled separately after this
		this.renderConfigurableList()
		this.setupConfigurableListListeners()

		this.renderElse()
	},

	renderConfigurableList: function() {
		let firstStatements = _.initial(this.options.statements)
		this.conditionalStatementEditors = []
		this.configurableList = new ConfigurableList({
			addLabel: this.getAddLabel.bind(this),
			addButtonExtraClass: 'add-list-button',
			customButton: true,
			itemData: firstStatements,
			allowLastItemDeletion: true,
			itemViewCreator: (statement, index) => {
				let editor = new ConditionalStatementEditor({
					formView: this.formView,
					statement,
					index,
					conditionEditorCreator: this.options.conditionEditorCreator,
					consequencesEditorCreator: this.options.consequencesEditorCreator
				})
				this.conditionalStatementEditors.push(editor)
				return editor
			}
		})
		this.$('.configurable-list-container').append(this.configurableList.el)
		this.configurableList.render()
		this.configurableList.finishRendering()
		_.each(this.conditionalStatementEditors, editor => {
			editor.conjunctionEditor.finishRendering()
			editor.consequencesEditor.finishRendering()
		})
		// Add a clickable area that will make toggling the collapsed state easier.
		this.configurableList.$('.conditional-statement-editor')
			.before(collapseToggle)

		// #53267 adding and removing list items causes the outer draggable
		// list to re-render, so we need to re-render the inner ones as well to
		// make sure the correct things are draggable
		this.configurableList.on('add-list-item delete-list-item', () => {
			_.each(this.conditionalStatementEditors, editor => {
				editor.conjunctionEditor.renderDraggableList()
			})
		})
	},

	setupConfigurableListListeners: function() {
		this.configurableList.on('add-list-item delete-list-item', () => {
			this.updateAddLabel()
			this.updateStatementTitles()
			this.toggleElseTitle()

			// https://github.com/SpiderStrategies/Scoreboard/pull/53884#issuecomment-1624057503
			// there are a lot of events fired when adding/deleting list items
			// so give everything a second to render correctly before setting
			// the height
			process.nextTick(() => this.resetFixedHeight())

			this.formView.change()
		})
		this.configurableList.on('add-list-item', () => {
			this.configurableList.$el
				.find('> .list-container > .suggestions > .item:last').prepend(collapseToggle)
		})
		this.configurableList.on('delete-list-item', (e, options = {}) => {
			if (options.view) {
				_.remove(this.conditionalStatementEditors, options.view)
				options.view.remove()
			}
		})
		// #53190 need to use dndstop instead of move so that the traveler is
		// gone and we're working with the correct elements in the dom
		this.configurableList.on('dndstop', () => {
			this.updateStatementTitles()
			this.formView.change()
		})
	},

	renderElse: function() {
		let elseConsequences = _.last(this.options.statements)?.consequences
		this.elseConsequencesEditor = this.options.consequencesEditorCreator?.(
			elseConsequences,
			{
				allowedActionTypes: this.options?.allowedActionTypes,
				isElseStatement: true
			}
		)
		this.$('.else').append(this.elseConsequencesEditor?.el)
		this.elseConsequencesEditor?.render()
		let titleHtml =
			collapseToggle +
			`<div class="title">${icon('collapsed')}${i18n.get('logic.else')}</div>`
		this.elseConsequencesEditor?.$el.prepend(titleHtml)
		this.elseConsequencesEditor?.finishRendering()
	},

	toggleCollapse: function(e) {
		const itemEl = this.$(e.target).parent()
		const isCollapsed = itemEl.hasClass('is-collapsed')

		// if we are collapsing a section, attach the most up-to-date data
		// on the dom element so we can access if needed
		if (!isCollapsed) {
			// mark this statement to indicate there are fields needing to be
			// filled out
			const markedFields = formHelper.getMarkedFields(itemEl)
			const missingRequiredFields = _.some(markedFields, mf =>
				!_.isNil(mf.test()))
			itemEl.toggleClass('fields-required', missingRequiredFields)


			// add data to the el so we can access if needed without reading
			// from the dom
			const statementEl = itemEl.find('.conditional-statement-editor')
			const statementData = statementEl.length == 0 ?
				this.elseConsequencesEditor?.readFromDom?.() :
				statementEl.data('view').readFromDom()
			itemEl.data('statements', statementData)
		}
		itemEl.toggleClass('is-collapsed', !isCollapsed)
		this.resetFixedHeight()
		this.formView.clearErrors()
	},

	showRemainingCollapsedFields: function() {
		// show any statements with fields that need to be filled out
		this.$('li.item.fields-required').removeClass('is-collapsed')
		const editorEl = this.elseConsequencesEditor?.$el
		if (editorEl?.hasClass('fields-required')) {
			editorEl.removeClass('is-collapsed')
		}
	},

	toggleElseTitle: function() {
		let onlyElse = isOnlyElse(this.readFromDom())
		this.elseConsequencesEditor?.$el
			.toggleClass('is-hiding-title', onlyElse)
			.removeClass('is-collapsed', !onlyElse)
	},

	getAddLabel: function() {
		let onlyElse = isOnlyElse(this.readFromDom())
		let key = onlyElse ? 'logic.addIf' : 'logic.addElseIf'
		return i18n.get(key)
	},

	updateAddLabel: function() {
		this.configurableList.$('> .add-list-item > .button-label').text(this.getAddLabel())
	},

	updateStatementTitles: function() {
		_.each(this.$('.conditional-statement-editor'), (el, index) => {
			this.$(el).data('view')?.renderTitle?.(index)
		})
	},

	// When the mouse enters the editor, store the height of the editor and
	// set that value as a fixed height on the editor.
	setFixedHeight: function() {
		this.$el.css('height', this.$el.height())
	},

	// When the mouse leaves the editor, remove the fixed height.
	removeFixedHeight: function() {
		this.$el.css('height', '')
	},

	resetFixedHeight: function() {
		// Remove the fixed height so the height can be recalculated
		this.removeFixedHeight()
		// Reset the fixed height in case the user drags and item
		this.setFixedHeight()
	},

	/**
	 * @return {ConditionalStatement[]}
	 */
	readFromDom: function() {
		let statements = _.map(this.$('.conditional-statement-editor'), el => {
			const itemEl = this.$(el).closest('li.item')
			if (itemEl.hasClass('is-collapsed')) return itemEl.data('statements')
			return this.$(el).data('view').readFromDom()
		})

		let elseConsequences = null
		if (this.elseConsequencesEditor) {
			if (this.elseConsequencesEditor.$el.hasClass('is-collapsed')) {
				elseConsequences = this.elseConsequencesEditor.$el.data('statements')
			} else {
				elseConsequences = this.elseConsequencesEditor?.readFromDom?.()
			}
		}
		statements.push({ consequences: elseConsequences })
		return statements
	},

	test: function() {
		for (let i = 0; i < this.conditionalStatementEditors?.length ?? 0; i++) {
			let test = this.conditionalStatementEditors[i].test?.()
			if (test) return test
		}
		return this.elseConsequencesEditor?.test?.()
	},

	autoRemove: true

})

/**
 * Returns whether the given collection of statements only represent a single
 * block of else consequences.
 * @param {ConditionalStatement[]} [statements]
 */
function isOnlyElse(statements) {
	if (_.size(statements) == 0) {
		return true
	}
	if (_.size(statements) > 1) {
		return false
	}
	return _.isEmpty(statements[0].conditions)
}

export default Logician
export { isOnlyElse }

Logician.__filename__ = 'components/logic/logician.js'
